import { Currency } from '@/api/types/public';
import { getDiscountCouponText } from '@/components/Coupon/handler';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { IS_DE_FR } from '@/utils/constants';
import {
  EOrderStatus,
  EPaymentMethodDisplayName,
  EShippingMethod,
  EShippingMethodDisplayName,
} from '@/utils/enums/order';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

interface OrderSummaryProps {
  orderInfo: any;
  // fullSymbol?: string;
  singleSymbol?: string;
  orderUpdateList?: any[];
  isChangeOrder?: boolean;
  isShowPoints?: boolean;
  noContainerPadding?: boolean;
  isShowDivider?: boolean;
  isFontBold?: boolean;
  containerClassName?: string;
  isInInvoiceDialog?: boolean;
  // isTransaction?: boolean;
}

export default function OrderSummary({
  orderInfo,
  // fullSymbol,
  singleSymbol,
  orderUpdateList,
  isChangeOrder,
  isShowPoints = true,
  noContainerPadding = false,
  isShowDivider = false,
  isFontBold = true,
  containerClassName = '',
  isInInvoiceDialog = false,
  // isTransaction,
}: OrderSummaryProps) {
  const t = useTranslations('account.orderSummary');
  const couponT = useTranslations('coupons');
  const shoppingT = useTranslations('account.myOrders.orderDetail');
  const locale = useLocale();
  const tShippingMethod = useTranslations('account.myOrders.orderDetail.shippingMethod');
  const [showCouponDes, setShowCouponDes] = useState(false);
  const [couponDes, setCouponDes] = useState('');
  logger.log('orderInfo', singleSymbol);

  const currencyList = useStore((state) => state.currencyList);
  const currency = currencyList.find(
    (item) => item.name === env.NEXT_PUBLIC_DEFAULT_CURRENCY,
  ) as Currency;

  const getFreightTypeShow = (_orderData: any) => {
    // 如果运费为0，并且快递方式是ordinary 展示Free Shipping
    let text = '';
    if (_orderData?.freight_price === '0.00' && _orderData?.freight_type === 'ordinary') {
      text = EShippingMethodDisplayName(EShippingMethod.ORDINARY, shoppingT);
      if (locale !== 'en') {
        text = tShippingMethod('ordinary');
      }
    } else {
      text = _orderData?.logistics_type;
    }
    // logistics_type 需要联系后端同学修改，前端只做拼接，影响到 standard shipping
    if (text && _orderData.freight_description) {
      text = `${text} (${_orderData.freight_description} ${tShippingMethod('days')})`;
    }
    return text;
  };

  useEffect(() => {
    if (!orderInfo?.discount_code_value) {
      setCouponDes('');
      return;
    }
    let text = getDiscountCouponText(orderInfo.discount_code_value, couponT, currency);
    if (text.includes(',')) {
      text = text.replace(/,/g, '<br>');
    }
    setCouponDes(text);
  }, [orderInfo]);

  // 获取支付方式显示
  const getPaymentMethodDisplay = () => {
    // 这里需要根据实际情况实现支付方式映射
    return EPaymentMethodDisplayName[orderInfo?.payment_method] || '';
  };

  const currentOrderUpdateList = orderUpdateList || orderInfo?.order_update_list;

  const classes = IS_DE_FR
    ? `text-14 notranslate ${isInInvoiceDialog ? 'w-[200px]' : 'w-[220px]'} text-right`
    : 'text-14 notranslate w-[250px] text-right';

  let widthClass = 'w-[250px]';
  if (IS_DE_FR) {
    widthClass = isInInvoiceDialog ? 'w-[200px]' : 'w-[220px]';
  }

  return (
    <>
      <WebOnly>
        <div className="p-16 rounded-10 bg-white text-14">
          <div className={`grand-total-inner flex flex-col gap-10 mt-24 ${containerClassName}`}>
            {/* 应付 */}
            <div className="flex justify-end items-center mb-10">
              <span className={`text-14 ${isFontBold ? 'font-bb' : ''}`}>{t('subtotal')}</span>
              <span className={classes}>
                {formatPriceNumberWithSymbol(orderInfo?.original_total_price, orderInfo?.currency)}
              </span>
            </div>

            {/* 第二件半价 */}
            {orderInfo?.rule_discounts_price && orderInfo?.rule_discounts_price !== '0.00' && (
              <div className="flex justify-end items-center mb-10">
                <span className={`text-14 ${isFontBold ? 'font-bb' : ''}`}>
                  {orderInfo.member_discount_rule_text
                    ? `${orderInfo.member_discount_rule_text} ${orderInfo.member_discount_rule_rate}% ${t('off')}`
                    : t('bogo50Off')}
                </span>
                <span className={classes}>
                  -
                  {formatPriceNumberWithSymbol(
                    orderInfo?.rule_discounts_price,
                    orderInfo?.currency,
                  )}
                </span>
              </div>
            )}

            {/* code优惠券 */}
            {orderInfo?.code_discounts_price && orderInfo?.code_discounts_price !== '0.00' && (
              <>
                <div className="flex justify-end items-center mb-10">
                  <span className={`text-14 ${isFontBold ? 'font-bb' : ''}`}>
                    {t('codeCoupon')}
                  </span>
                  <span
                    className={`text-14 notranslate w-[250px] text-right flex items-center justify-end ${couponDes ? 'cursor-pointer' : ''}`}
                    onClick={() => couponDes && setShowCouponDes(!showCouponDes)}
                  >
                    {Number(orderInfo.code_discounts_price) >= 0 ? (
                      <>
                        -
                        {formatPriceNumberWithSymbol(
                          orderInfo?.code_discounts_price,
                          orderInfo?.currency,
                        )}
                      </>
                    ) : (
                      <>
                        +
                        {formatPriceNumberWithSymbol(
                          orderInfo?.code_discounts_price.replace(/-/, ''),
                          orderInfo?.currency,
                        )}
                      </>
                    )}
                    {couponDes && (
                      <CollapseArrowDownOutlined
                        className={`ml-4 transition-transform duration-300 ${showCouponDes ? 'rotate-180' : ''}`}
                      />
                    )}
                  </span>
                </div>
                {couponDes && showCouponDes && (
                  <div className="flex justify-end mt-[-10px]">
                    <div className="mb-10 px-10 py-8 bg-[#F5F6F7] rounded-6 w-fit transition-opacity duration-300 ease-in-out opacity-100 animate-fade-in">
                      <span className="text-13" dangerouslySetInnerHTML={{ __html: couponDes }} />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* 积分兑换 */}
            {orderInfo?.point_discounts_price && orderInfo?.point_discounts_price !== '0.00' && (
              <div className="flex justify-end items-center mb-10">
                <span className={`text-14 ${isFontBold ? 'font-bb' : ''}`}>
                  {t('redeemPoints', { point: orderInfo?.point })}
                </span>
                <span className={classes}>
                  -
                  {formatPriceNumberWithSymbol(
                    orderInfo?.point_discounts_price,
                    orderInfo?.currency,
                  )}
                </span>
              </div>
            )}

            {/* vip订单 */}
            {orderInfo?.vip_order && (
              <div className="flex justify-end items-center mb-10">
                <span className={`text-14 ${isFontBold ? 'font-bb' : ''}`}>{t('zingMember')}</span>
                <span className={classes}>
                  {formatPriceNumberWithSymbol(orderInfo?.vip_order?.payable, orderInfo?.currency)}
                </span>
              </div>
            )}

            {/* 运费 */}
            {orderInfo?.freight_origin_price && (
              <div className="flex justify-end items-center mb-10">
                <span className={`text-14 ${isFontBold ? 'font-bb' : ''} text-right`}>
                  {getFreightTypeShow(orderInfo)}
                </span>
                <span className={classes}>
                  {formatPriceNumberWithSymbol(
                    orderInfo?.freight_origin_price,
                    orderInfo?.currency,
                  )}
                </span>
              </div>
            )}

            {/* 运费优惠 */}
            {orderInfo?.freight_discount_type > 1 && orderInfo?.freight_discount_type !== 5 && (
              <div className="flex justify-end items-center mb-10">
                <span className={`text-14 ${isFontBold ? 'font-bb' : ''}`}>
                  {orderInfo?.freight_discount_type === 2 &&
                    t('freeShippingOver', {
                      threshold: formatPriceNumberWithSymbol(
                        orderInfo?.freight_threshold,
                        orderInfo?.currency,
                      ),
                    })}
                  {orderInfo?.freight_discount_type === 3 && t('freeShippingZing')}
                  {orderInfo?.freight_discount_type === 4 && t('shippingCoupon')}
                </span>
                <span className={classes}>
                  -
                  {formatPriceNumberWithSymbol(
                    orderInfo?.freight_discount_type === 4
                      ? orderInfo?.freight_discounts_price
                      : orderInfo?.freight_origin_price,
                    orderInfo?.currency,
                  )}
                </span>
              </div>
            )}

            {/* 税费 */}
            {orderInfo?.tax_price && orderInfo?.tax_price !== '0.00' && (
              <div className="flex justify-end items-center mb-10">
                <span className={`text-14 ${isFontBold ? 'font-bb' : ''}`}>
                  {orderInfo?.tax_name || t('estimatedTaxes')}
                </span>
                <span className={classes}>
                  {formatPriceNumberWithSymbol(orderInfo?.tax_price, orderInfo?.currency)}
                </span>
              </div>
            )}

            {/* 物流保险 */}
            {orderInfo?.insurance_info &&
              orderInfo?.insurance_info.is_insurance === 1 &&
              orderInfo?.insurance_price !== '0.00' && (
                <div className="flex justify-end items-center mb-10">
                  <span className={`text-14 ${isFontBold ? 'font-bb' : ''}`}>
                    {t('worryFreeDelivery')}
                  </span>
                  <span className={classes}>
                    {formatPriceNumberWithSymbol(orderInfo?.insurance_price, orderInfo?.currency)}
                  </span>
                </div>
              )}

            {/* 退货 */}
            {orderInfo?.is_change_order &&
              currentOrderUpdateList?.[0]?.all_refund_amount &&
              +currentOrderUpdateList[0].all_refund_amount > 0 && (
                <div className="flex justify-end items-center mb-10">
                  <span className={`text-14 ${isFontBold ? 'font-bb' : ''}`}>{t('refund')}</span>
                  <span className={classes}>
                    -
                    {formatPriceNumberWithSymbol(
                      currentOrderUpdateList[0].all_refund_amount,
                      orderInfo?.currency,
                    )}
                  </span>
                </div>
              )}

            {/* 补差价 */}
            {orderInfo?.is_change_order &&
              currentOrderUpdateList?.[0]?.all_price_difference_amount &&
              +currentOrderUpdateList[0].all_price_difference_amount > 0 && (
                <div className="flex justify-end items-center mb-10">
                  <span className={`text-14 ${isFontBold ? 'font-bb' : ''}`}>
                    {t('priceDifferencePaid')}
                  </span>
                  <span className={classes}>
                    {formatPriceNumberWithSymbol(
                      currentOrderUpdateList[0].all_price_difference_amount,
                      orderInfo?.currency,
                    )}
                  </span>
                </div>
              )}

            {/* 合计 */}
            <div
              className={`flex justify-end items-center mb-10 ${isShowDivider ? 'border-t border-[#DCE0E8] pt-10' : ''}`}
            >
              <span className={`text-16 ${isFontBold ? 'font-bb' : ''}`}>{t('grandTotal')}</span>
              <span className={`text-20 notranslate text-right font-bb ${widthClass}`}>
                {orderInfo?.is_change_order && currentOrderUpdateList?.[0] ? (
                  <>
                    {formatPriceNumberWithSymbol(
                      orderUpdateList[0].actual_payment,
                      orderInfo?.currency,
                    )}
                  </>
                ) : (
                  <>{formatPriceNumberWithSymbol(orderInfo?.payable, orderInfo?.currency)}</>
                )}
              </span>
            </div>

            {/* 是否使用钱包 */}
            {orderInfo?.is_wallet === 1 && (
              <div className="mb-10 p-12 rounded-6 bg-gray-50 w-fit self-end">
                <div className="flex justify-end items-center">
                  <span className="text-14">{t('storeCredit')}</span>
                  <span className={classes}>
                    {formatPriceNumberWithSymbol(orderInfo?.wallet_payment, orderInfo?.currency)}
                  </span>
                </div>
                {orderInfo?.external_payment !== '0.00' &&
                  orderInfo?.status !== EOrderStatus.UNPAID && (
                    <div className="flex justify-end items-center mt-6">
                      <span className="text-14">{getPaymentMethodDisplay()}</span>
                      <span className={classes}>
                        {formatPriceNumberWithSymbol(
                          orderInfo?.external_payment,
                          orderInfo?.currency,
                        )}
                      </span>
                    </div>
                  )}
              </div>
            )}

            {/* 积分获取信息 */}
            {orderInfo?.order_type !== 4 &&
              orderInfo?.order_type !== 12 &&
              orderInfo?.gain_point > 0 &&
              isShowPoints && (
                <div className="text-right mt-10">
                  <span
                    className="text-13 text-warning"
                    dangerouslySetInnerHTML={{
                      __html: t('pointsEarned', {
                        points: orderInfo?.gain_point.toString(),
                        pointText: orderInfo?.gain_point > 1 ? t('points') : t('point'),
                      }).replace(
                        orderInfo?.gain_point.toString(),
                        `<span class="text-black font-medium">${orderInfo?.gain_point}</span>`,
                      ),
                    }}
                  />
                </div>
              )}

            {/* 积分商城订单不获得积分 */}
            {orderInfo?.order_type === 14 && (
              <div className="text-right mt-10">
                <span className="text-13 text-warning">{t('noPointsRewardsStore')}</span>
              </div>
            )}
          </div>
        </div>
      </WebOnly>
      <WapOnly>
        <div
          className={`w-[96%]  rounded-10 bg-white text-13 ${noContainerPadding ? 'p-0' : 'p-12'}`}
        >
          <div className="price-item flex justify-between items-center">
            <div className="price-item-title">{t('subtotal')}</div>
            <div className="price-item-value">
              {formatPriceNumberWithSymbol(orderInfo?.original_total_price, orderInfo?.currency)}
            </div>
          </div>
          {/* 优惠规则 */}
          {orderInfo?.rule_discounts_price && orderInfo?.rule_discounts_price !== '0.00' && (
            <div className="price-item mt-10 flex justify-between items-center">
              <div className="price-item-title">
                {orderInfo.member_discount_rule_text
                  ? `${orderInfo.member_discount_rule_text} ${orderInfo.member_discount_rule_rate}% ${t('off')}`
                  : t('bogo50Off')}
              </div>
              <div className="price-item-value notranslate">
                -{formatPriceNumberWithSymbol(orderInfo?.rule_discounts_price, orderInfo?.currency)}
              </div>
            </div>
          )}

          {/* code优惠券 */}
          {orderInfo?.code_discounts_price && orderInfo?.code_discounts_price !== '0.00' && (
            <div className="price-item mt-10  flex justify-between items-center">
              <div className="price-item-title">{t('codeCoupon')}</div>
              <div
                className="price-item-value flex items-center notranslate"
                onClick={() => setShowCouponDes(!showCouponDes)}
              >
                -{formatPriceNumberWithSymbol(orderInfo?.code_discounts_price, orderInfo?.currency)}
                {couponDes && (
                  <CollapseArrowDownOutlined
                    className={`ml-4 transition-transform duration-300 ${showCouponDes ? 'rotate-180' : ''}`}
                  />
                )}
              </div>
            </div>
          )}

          {couponDes && showCouponDes && (
            <div className="flex justify-end">
              <div className="mt-8 px-8 leading-28 bg-[#F5F6F7] rounded-6 w-fit transition-opacity duration-300 ease-in-out opacity-100 animate-fade-in">
                <span className="text-12" dangerouslySetInnerHTML={{ __html: couponDes }} />
              </div>
            </div>
          )}

          {/* 积分兑换 */}
          {orderInfo?.point_discounts_price && orderInfo?.point_discounts_price !== '0.00' && (
            <div className="price-item mt-10 flex justify-between items-center">
              <div className="price-item-title">
                {t('redeemPoints', { point: orderInfo?.point })}
              </div>
              <div className="price-item-value notranslate">
                -
                {formatPriceNumberWithSymbol(orderInfo?.point_discounts_price, orderInfo?.currency)}
              </div>
            </div>
          )}

          {/* vip订单 */}
          {orderInfo?.vip_order && (
            <div className="price-item mt-10 flex justify-between items-center">
              <div className="price-item-title">{t('zing')}</div>
              <div className="price-item-value notranslate">
                {formatPriceNumberWithSymbol(orderInfo?.vip_order?.payable, orderInfo?.currency)}
              </div>
            </div>
          )}

          {/* 运费 */}
          {orderInfo?.freight_origin_price && (
            <div className="price-item mt-10 flex justify-between items-center">
              <div className="price-item-title">{getFreightTypeShow(orderInfo)}</div>
              <div className="price-item-value notranslate">
                {formatPriceNumberWithSymbol(orderInfo?.freight_origin_price, orderInfo?.currency)}
              </div>
            </div>
          )}

          {/* 运费优惠 */}
          {orderInfo?.freight_discount_type > 1 && orderInfo?.freight_discount_type !== 5 && (
            <div className="price-item mt-10 flex justify-between items-center">
              <div className="price-item-title notranslate">
                {orderInfo?.freight_discount_type === 2 &&
                  t('freeShippingOver', {
                    threshold: formatPriceNumberWithSymbol(
                      orderInfo?.freight_threshold,
                      orderInfo?.currency,
                    ),
                  })}
                {orderInfo?.freight_discount_type === 3 && t('freeShippingZing')}
                {orderInfo?.freight_discount_type === 4 && t('shippingCoupon')}
              </div>
              <div className="price-item-value notranslate">
                -
                {formatPriceNumberWithSymbol(
                  orderInfo?.freight_discount_type === 4
                    ? orderInfo?.freight_discounts_price
                    : orderInfo?.freight_origin_price,
                  orderInfo?.currency,
                )}
              </div>
            </div>
          )}

          {/* 税费 */}
          {orderInfo?.tax_price && orderInfo?.tax_price !== '0.00' && (
            <div className="price-item mt-10 flex justify-between items-center">
              <div className="price-item-title">{orderInfo?.tax_name || t('estimatedTaxes')}</div>
              <div className="price-item-value notranslate">
                {formatPriceNumberWithSymbol(orderInfo?.tax_price, orderInfo?.currency)}
              </div>
            </div>
          )}

          {/* 物流保险 */}
          {orderInfo?.insurance_info &&
            orderInfo?.insurance_info.is_insurance === 1 &&
            orderInfo?.insurance_price !== '0.00' && (
              <div className="price-item mt-10 flex justify-between items-center">
                <div className="price-item-title">{t('worryFreeDelivery')}</div>
                <div className="price-item-value notranslate">
                  {formatPriceNumberWithSymbol(orderInfo?.insurance_price, orderInfo?.currency)}
                </div>
              </div>
            )}

          {/* 退货 TODO: */}
          {isChangeOrder &&
            orderUpdateList?.[0]?.all_refund_amount &&
            +orderUpdateList[0].all_refund_amount > 0 && (
              <div className="price-item mt-10 flex justify-between items-center">
                <div className="price-item-title">{t('refund')}</div>
                <div className="price-item-value notranslate">
                  -
                  {formatPriceNumberWithSymbol(
                    orderUpdateList[0].all_refund_amount,
                    orderInfo?.currency,
                  )}
                </div>
              </div>
            )}

          {/* 补差价 TODO: 待开发 */}
          {isChangeOrder &&
            currentOrderUpdateList?.[0]?.all_price_difference_amount &&
            +currentOrderUpdateList[0].all_price_difference_amount > 0 && (
              <div className="price-item mt-10 flex justify-between items-center">
                <div className="price-item-title">{t('priceDifferencePaid')}</div>
                <div className="price-item-value notranslate">
                  {formatPriceNumberWithSymbol(
                    currentOrderUpdateList[0].all_price_difference_amount,
                    orderInfo?.currency,
                  )}
                </div>
              </div>
            )}

          {/* 实际付款总额 */}
          <div
            className={`price-item mt-10 flex justify-between items-center ${isShowDivider ? 'border-t border-[#DCE0E8] pt-20' : ''}`}
          >
            <div className="price-item-title">{t('grandTotal')}</div>
            <div className="price-item-value text-red font-bb notranslate">
              {isChangeOrder && orderUpdateList?.[0] ? (
                <>
                  {formatPriceNumberWithSymbol(
                    orderUpdateList[0].actual_payment,
                    orderInfo?.currency,
                  )}
                </>
              ) : (
                <>{formatPriceNumberWithSymbol(orderInfo?.payable, orderInfo?.currency)}</>
              )}
            </div>
          </div>

          {/* 是否使用钱包 */}
          {orderInfo?.is_wallet === 1 && (
            <div className="price-item mt-8 flex justify-between items-center">
              <div className="wallet-item w-full bg-[#f5f6f7] rounded-[5px] p-8 text-11">
                <p className="flex justify-between">
                  <span>{t('storeCredit')}</span>
                  <span className="notranslate">
                    {formatPriceNumberWithSymbol(orderInfo?.wallet_payment, orderInfo?.currency)}
                  </span>
                </p>
                {orderInfo?.external_payment !== '0.00' &&
                  orderInfo?.status !== EOrderStatus.UNPAID && (
                    <p className="flex justify-between other-pay mt-10">
                      <span>{getPaymentMethodDisplay()}</span>
                      <span className="notranslate">
                        {formatPriceNumberWithSymbol(
                          orderInfo?.external_payment,
                          orderInfo?.currency,
                        )}
                      </span>
                    </p>
                  )}
              </div>
            </div>
          )}

          {/* 积分获取信息 */}
          {orderInfo?.order_type !== 4 &&
            orderInfo?.order_type !== 12 &&
            orderInfo?.gain_point > 0 &&
            isShowPoints && (
              <div className="gain-item mt-10 text-right">
                <span
                  className="gain text-12 text-warning"
                  dangerouslySetInnerHTML={{
                    __html: t('pointsEarned', {
                      points: orderInfo?.gain_point.toString(),
                      pointText: orderInfo?.gain_point > 1 ? t('points') : t('point'),
                    }).replace(
                      orderInfo?.gain_point.toString(),
                      `<span class="text-primary-black">${orderInfo?.gain_point}</span>`,
                    ),
                  }}
                />
              </div>
            )}

          {/* 积分商城订单不获得积分 */}
          {orderInfo?.order_type === 14 && (
            <div className="gain-item mt-12 relative text-right w-[102%] left-[-2%]">
              <span className="gain text-11 text-warning leading-16">
                {t('noPointsRewardsStore')}
              </span>
            </div>
          )}
        </div>
      </WapOnly>
    </>
  );
}
